import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import PageMeta from '@/components/base/PageMeta';
import { articles, categories, type Article } from '@/mocks/articles';

function findArticle(id: number): Article | undefined {
  return articles.find((a) => a.id === id);
}

function getRelatedArticles(current: Article, count: number = 3): Article[] {
  return articles
    .filter((a) => a.id !== current.id && a.category === current.category)
    .slice(0, count);
}

function getCategoryName(slug: string): string {
  const cat = categories.find((c) => c.slug === slug);
  return cat?.name ?? slug;
}

const categoryNameMap: Record<string, string> = {
  guide: '使用指南',
  faq: '常见问题',
  security: '安全公告',
  news: '平台动态',
};

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const articleId = Number(id);
  const article = findArticle(articleId);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto rounded-full bg-secondary-100 flex items-center justify-center mb-4">
              <i className="ri-file-search-line text-2xl text-foreground-300" />
            </div>
            <h2 className="text-xl font-heading font-bold text-foreground-800 mb-2">文章未找到</h2>
            <p className="text-sm text-foreground-500 mb-6">您访问的文章不存在或已被移除</p>
            <Link
              to="/"
              className="inline-flex px-6 py-2.5 rounded-lg bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const related = getRelatedArticles(article);
  const catName = categoryNameMap[article.category] ?? article.category;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://98tang.com/' },
          { '@type': 'ListItem', position: 2, name: catName, item: `https://98tang.com/category/${article.category}` },
          { '@type': 'ListItem', position: 3, name: article.title },
        ],
      },
      {
        '@type': 'Article',
        headline: article.title,
        description: article.excerpt,
        datePublished: article.date,
        url: `https://98tang.com/article/${article.id}`,
        author: { '@type': 'Organization', name: '98堂' },
        publisher: { '@type': 'Organization', name: '98堂' },
      },
    ],
  };

  return (
    <>
      <PageMeta
        title={`${article.title} - 98堂 入口`}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://98tang.com/article/${article.id}`}
        ogType="article"
        schema={articleSchema}
      />
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-50 border-b border-secondary-100">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: catName, href: `/category/${article.category}` },
                { label: article.title },
              ]}
            />
          </div>
        </div>

        <article className="max-w-3xl mx-auto px-4 md:px-6 py-10">
          <header className="mb-8">
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <Link
                to={`/category/${article.category}`}
                className="px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium hover:bg-accent-200 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                {catName}
              </Link>
              <span className="text-xs text-foreground-400">{article.date}</span>
              <span className="text-xs text-foreground-400 flex items-center gap-1">
                <i className="ri-eye-line" />
                {article.views.toLocaleString()} 次阅读
              </span>
              <span className="text-xs text-foreground-400 flex items-center gap-1">
                <i className="ri-time-line" />
                {article.readTime}
              </span>
            </div>
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 leading-snug mb-4">
              {article.title}
            </h1>
            <div className="flex flex-wrap gap-2">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-2.5 py-1 rounded-md bg-secondary-100 text-secondary-700 text-xs"
                >
                  {tag}
                </span>
              ))}
            </div>
          </header>

          <div
            className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed
              prose-h3:text-foreground-900 prose-h3:font-semibold prose-h3:mt-8 prose-h3:mb-4
              prose-p:mb-4 prose-ul:mb-4 prose-li:mb-1
              prose-strong:text-foreground-900 prose-strong:font-semibold"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="mt-12 pt-8 border-t border-secondary-200/60">
            <p className="text-sm text-foreground-500 text-center">
              声明：本文仅供信息参考，请以98堂平台官方公告为准。
            </p>
          </div>
        </article>

        {related.length > 0 && (
          <section className="bg-secondary-50 py-14">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h3 className="text-lg font-heading font-bold text-foreground-950 mb-6 text-center">
                相关文章
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {related.map((rel) => (
                  <Link
                    key={rel.id}
                    to={`/article/${rel.id}`}
                    className="block p-5 rounded-lg bg-background-50 border border-secondary-200/60 hover:border-primary-200 transition-all duration-300 cursor-pointer group"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <span className="px-2 py-0.5 rounded-md bg-accent-100 text-accent-700 text-xs font-medium">
                        {categoryNameMap[rel.category] ?? rel.category}
                      </span>
                      <span className="text-xs text-foreground-400">{rel.date}</span>
                    </div>
                    <h4 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors duration-200 line-clamp-2">
                      {rel.title}
                    </h4>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}